import { useRef, useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import CaseStudyCard from "@/components/CaseStudyCard";
import type { CaseStudyCardData } from "@/components/CaseStudyCard";
import { supabase } from "@/integrations/supabase/client";
import { caseStudies as fallbackStudies } from "@/data/caseStudies";
import excapLogo from "@/assets/case-studies/excap-logo.png";
import { motion } from "framer-motion";

const slugToFallbackId: Record<string, string> = {
  "whop-highered": "whop",
  "c23-labs": "c23",
  "empire": "empire",
  "humming-ai": "humming",
  "m-r": "mer",
  "chill-bites-drinks": "chill",
  "exceptional-100-framer-site-development": "exceptional",
};

const fallbackLogoMap: Record<string, string> = {};
fallbackStudies.forEach((s) => {
  fallbackLogoMap[s.id] = s.logo;
});

const extraLogos: Record<string, string> = {
  "exceptional-100-framer-site-development": excapLogo,
};

interface MoreWorkCarouselProps {
  /** Current case study slug to exclude from recommendations */
  currentSlug: string;
  /** Minimum number of cards to show (default 4) */
  minCards?: number;
}

const MoreWorkCarousel = ({ currentSlug, minCards = 4 }: MoreWorkCarouselProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [studies, setStudies] = useState<CaseStudyCardData[]>([]);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const autoTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isPausedRef = useRef(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  }, []);

  const scrollBy = useCallback((dir: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = window.innerWidth < 768 ? 300 : 340;
    el.scrollBy({ left: dir === "left" ? -cardWidth : cardWidth, behavior: "smooth" });
  }, []);

  // Auto-advance
  const startAutoPlay = useCallback(() => {
    if (autoTimerRef.current) clearInterval(autoTimerRef.current);
    autoTimerRef.current = setInterval(() => {
      if (isPausedRef.current) return;
      const el = scrollRef.current;
      if (!el) return;
      const atEnd = el.scrollLeft >= el.scrollWidth - el.clientWidth - 10;
      if (atEnd) {
        el.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        scrollBy("right");
      }
    }, 5000);
  }, [scrollBy]);

  useEffect(() => {
    startAutoPlay();
    return () => {
      if (autoTimerRef.current) clearInterval(autoTimerRef.current);
    };
  }, [startAutoPlay]);

  // Pause on touch/pointer interaction
  const pauseAuto = useCallback(() => {
    isPausedRef.current = true;
  }, []);

  const resumeAuto = useCallback(() => {
    isPausedRef.current = false;
    startAutoPlay(); // Reset timer
  }, [startAutoPlay]);

  const handleArrowClick = useCallback(
    (dir: "left" | "right") => {
      pauseAuto();
      scrollBy(dir);
      // Resume after a delay
      setTimeout(resumeAuto, 8000);
    },
    [scrollBy, pauseAuto, resumeAuto]
  );

  // Fetch studies
  useEffect(() => {
    const hiddenSlugs = ["empire"];
    const fetchStudies = async () => {
      const { data, error } = await supabase
        .from("case_studies")
        .select("id, slug, title, subtitle, category, cover_image_url, logo_url, industry_tag")
        .neq("slug", currentSlug)
        .not("slug", "in", `("${hiddenSlugs.join('","')}")`)
        .order("display_order", { ascending: true })
        .limit(Math.max(minCards, 8));

      if (!error && data && data.length > 0) {
        setStudies(
          data.map((d) => {
            const fallbackId = slugToFallbackId[d.slug] || d.slug;
            const logo = d.logo_url || extraLogos[d.slug] || fallbackLogoMap[fallbackId] || "";
            return {
              id: d.id,
              slug: d.slug,
              title: d.title,
              subtitle: d.subtitle || "",
              category: d.category || "",
              industry_tag: d.industry_tag,
              image: d.cover_image_url || "",
              logo,
            };
          })
        );
      }
    };
    fetchStudies();
  }, [currentSlug, minCards]);

  if (studies.length === 0) return null;

  return (
    <div className="section-dark py-16 overflow-visible">
      <div className="px-5 md:px-10 lg:px-14 mb-8 flex items-baseline justify-between">
        <p className="font-body text-ts-2xs tracking-[0.25em] uppercase text-muted-foreground/60">
          More Work
        </p>
        <div className="hidden md:flex items-center gap-1">
          <button
            aria-label="Previous project"
            onClick={() => handleArrowClick("left")}
            disabled={!canScrollLeft}
            className="p-1.5 rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={14} />
          </button>
          <button
            aria-label="Next project"
            onClick={() => handleArrowClick("right")}
            disabled={!canScrollRight}
            className="p-1.5 rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        onScroll={checkScroll}
        onTouchStart={pauseAuto}
        onTouchEnd={() => setTimeout(resumeAuto, 8000)}
        onPointerDown={(e) => {
          if (e.pointerType === "touch") pauseAuto();
        }}
        onPointerUp={(e) => {
          if (e.pointerType === "touch") setTimeout(resumeAuto, 8000);
        }}
        className="flex gap-4 overflow-x-auto overflow-y-visible px-5 md:px-10 lg:px-14 pb-8 snap-x snap-mandatory md:snap-none"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {studies.map((study, i) => (
          <motion.div
            key={study.id || study.slug}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.06, duration: 0.4 }}
            className="flex-shrink-0 w-[75vw] max-w-[320px] md:w-[320px] snap-center md:snap-align-none"
          >
            <CaseStudyCard study={study} />
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default MoreWorkCarousel;
